import { Input, StarsButton, Translation } from '@ps/web-shared-components';
import { createForm } from '@ps/web-shared-ui';
import classNames from 'clsx';
import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import type { Dispatch } from '@swf/core/store';
import {
  PKR_RESTRICTED_SYMBOLS,
  PKR_RESTRICTED_SYMBOLS_STRING,
  mustNotContainSymbols,
  useCheckUserClubRole,
  useHomeGamesWidget
} from '@swf/poker';

import { ModalRoutes, UserClubRole } from '../../../types/common';
import BinIcon from '../Wall/assets/Bin.svg';

import AnnouncementInfo from './AnnouncementInfo/AnnouncementInfo';
import {
  TXTCLI_Edit,
  TXTCLI_Publish_the_announcements_to_t_ELL,
  TXTWCL_The_following_characters_are_i_ELL
} from './Announcements.messages.json';
import styles from './Announcements.scss';
import AnnouncementIcon from './assets/Announcement.svg';
import SendIcon from './assets/send.svg';
import useClubMessageRequest from './hooks/useClubMessageRequest';

export interface FormFields {
  announcement: string | null;
}

const Form = createForm<FormFields>();

const Announcements: React.FC = () => {
  const { clubId } = useParams<{ clubId: string }>();
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const { widgetSelect } = useHomeGamesWidget();
  const { formatMessage } = useIntl();

  const announcement = useSelector(widgetSelect.announcement);
  const { handleSubmit } = useClubMessageRequest({ clubId, setLoading });
  const userHasRights = useCheckUserClubRole(`${clubId}`, [UserClubRole.Manager]);
  const { modal } = useDispatch<Dispatch>();

  const isContentEmpty = !announcement || announcement?.content?.length === 0;
  const displayForm = userHasRights && (isEditMode || isContentEmpty);
  const displayAnnouncement = announcement && !displayForm;
  const inputPlaceholder = formatMessage(TXTCLI_Publish_the_announcements_to_t_ELL);

  useEffect(() => {
    setIsEditMode(false);
  }, [clubId]);

  const handleDeleteClick = useCallback(() => {
    modal.enqueue({
      path: ModalRoutes.deleteAnnouncementPrompt,
      locationState: { clubId }
    });
  }, [modal, clubId]);

  const sideControls = !userHasRights ? null : (
    <div className={classNames(styles.announcementControls, styles.controlsPosition)}>
      <span onClick={handleDeleteClick}>
        <BinIcon className={styles.icon} />
      </span>
    </div>
  );

  const controls = !userHasRights ? null : (
    <div className={styles.announcementControls}>
      <span onClick={() => setIsEditMode(true)}>
        <Translation {...TXTCLI_Edit} />
      </span>
    </div>
  );

  useEffect(() => {
    if (!loading && isEditMode) {
      setIsEditMode(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  return (
    <>
      {displayAnnouncement && (
        <div className={styles.container} data-testid={`announcements`}>
          <AnnouncementInfo
            announcementState={announcement}
            sideControls={sideControls}
            controls={controls}
          />
        </div>
      )}
      {displayForm && (
        <div data-testid={`announcements-form`}>
          <Form
            className={styles.form}
            onSubmit={handleSubmit}
            initialValues={{ announcement: announcement?.content ?? '' }}
            render={({ values: { announcement } }) => {
              const value = announcement ?? '';
              const isEmpty = value.replace(/ /g, '') === '';
              return (
                <>
                  <AnnouncementIcon width={42} />
                  <Input
                    id="announcement"
                    className={styles.input}
                    label=""
                    showValidationTick={false}
                    type="textarea"
                    placeholder={inputPlaceholder}
                    validators={[
                      mustNotContainSymbols(PKR_RESTRICTED_SYMBOLS, {
                        ...TXTWCL_The_following_characters_are_i_ELL,
                        values: { 0: PKR_RESTRICTED_SYMBOLS_STRING }
                      })
                    ]}
                  />
                  <StarsButton
                    data-testid={`announcements-button`}
                    block
                    icon={<SendIcon />}
                    disabled={loading || isEmpty}
                    variant="text"
                    type="submit"
                  />
                </>
              );
            }}
          />
        </div>
      )}
    </>
  );
};

export default Announcements;
